// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

// components
import Guidelines from '/react4xp/components/Guidelines'
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'
import { UxMenu, UxClose, UxSearch } from '/react4xp/components/Svg/Ux'

// utils
import { getFocusableElementsOutside, isNonWhitespaceString } from '/lib/reactutils/utils'

// props
interface Props {
	isPrototype?: boolean
	skipToMainLabel?: string
	logo?: string
	search?: {
		label: string
		href: string
	}
	fsMenu?: {
		button?: {
			label: string
			href: string
			target?: '_self' | '_blank'
		}
		cols: {
			label: string
			href: string
			target?: '_self' | '_blank'
		}[][]
	}
}

export default function Header({
	isPrototype = true, // this should be set to false in production
	skipToMainLabel = 'Gå til hovedinnhold',
	logo = '/images/svg/logo/logo.svg',
	search = {
		label: 'Søk',
		href: '#',
	},
	fsMenu = {
		button: {
			label: 'Bli medlem',
			href: '#',
			target: '_self'
		},
		cols: [
			[
				{
					label: 'Om oss',
					href: '#',
					target: '_self'
				},
				{
					label: 'Politikk',
					href: '#',
					target: '_self'
				},
				{
					label: 'Tillitsvalg',
					href: '#',
					target: '_self'
				},
				{
					label: 'Kalender',
					href: '#',
					target: '_self'
				},
				{
					label: 'Gi en gave',
					href: '#',
					target: '_self'
				}
			],
			[
				{
					label: 'Våre viktigste argument',
					href: '#',
					target: '_self'
				},
				{
					label: 'Hvorfor bli medlem?',
					href: '#',
					target: '_self'
				},
				{
					label: 'EØS-guiden',
					href: '#',
					target: '_self'
				},
				{
					label: 'Kampanjer',
					href: '#',
					target: '_self'
				},
				{
					label: 'Fylkeslag',
					href: '#',
					target: '_self'
				}
			],
			[
				{
					label: 'Publikasjoner',
					href: '#',
					target: '_self'
				},
				{
					label: 'Nettbutikk',
					href: '#',
					target: '_self'
				},
				{
					label: 'Skole',
					href: '#',
					target: '_self'
				},
				{
					label: 'Din side',
					href: '#',
					target: '_self'
				}
			]
		]
	}
}: Props) {

	// animation ref
	const menuAnimationRef = useRef(null)

	// open / close fs menu
	const [isShown, setIsShown] = useState(false)

	const openCloseMenu = () => {
		setIsShown(!isShown)
	}

	// menu animation
	useGSAP(() => {
		const menuAnimation = gsap.timeline({
			paused: true
		})

		menuAnimation.to('#fs-menu', {
			opacity: 1,
			visibility: 'visible',
			ease: 'none',
			duration: 0
		})

		menuAnimation.to('#fs-menu', {
			clipPath: 'inset(0% 0% 0% 0%)',
			ease: 'power2.inOut',
			duration: 1
		})

		menuAnimation.to('.open-fs-menu .open', {
			opacity: 0,
			ease: 'power2.inOut',
			duration: .6
		}, '-=1')

		menuAnimation.to('.open-fs-menu .close', {
			opacity: 1,
			ease: 'power2.inOut',
			duration: .6
		}, '-=1')

		menuAnimation.fromTo('#fs-menu .cols .menu li', {
			y: 50,
			autoAlpha: 0
		}, {
			y: 0,
			autoAlpha: 1,
			stagger: .05
		}, '-=.7')

		// store the animation timeline in the ref
		menuAnimationRef.current = menuAnimation
	})

	useEffect(() => {
		const focusable_els = document.getElementById('trap-focus')
		const focusableElementsOutside = getFocusableElementsOutside(focusable_els)

		// disable focus on all elements outside the fs-menu when it's open
		const disableFocusOutside = () => {
			focusableElementsOutside.forEach((el) => {
				el.setAttribute('tabindex', '-1')
			})
		}

		// enable focus on all elements outside the fs-menu when it's closed
		const enableFocusOutside = () => {
			focusableElementsOutside.forEach((el) => {
				el.removeAttribute('tabindex')
			})
		}

		setIsShown(isShown)

		if (menuAnimationRef.current) {
			if (isShown) {
				menuAnimationRef.current.play()
				document.body.style.overflowY = 'scroll'
				document.body.style.position = 'fixed'
				disableFocusOutside()
			} else {
				menuAnimationRef.current.reverse()
				document.body.style.overflowY = 'auto'
				document.body.style.position = 'relative'
				enableFocusOutside()
			}
		}
	}, [isShown])

	useEffect(() => {
		window.onpageshow = () => {
			menuAnimationRef.current.progress(0).pause()
			setIsShown(false)
		}
	}, [])

	return (
		<>

			{isPrototype && <Guidelines />}

			<Button
				href='#main'
				className='skip-to-main'
				color='primary'
				style='solid'
				small
			>
				{skipToMainLabel}
			</Button>

			<Section id='top-menu' bgColor='white'>
				<div id='trap-focus'>

					{/* top menu */}
					<div className='container relative z3'>

						<div className='wrapper'>

							<a
								href='/'
								className='logo'
							>
								<img
									src={logo}
									alt='Nei til EU'
									width={110}
									height={110}
								/>
							</a>

							<div className='right'>

								<form
									action={search?.href || '/pages/search'}
									className='search'
									method='get'
								>

									<button
										aria-label={search?.label}
										type='submit'
									>
										<UxSearch />
									</button>

									<input
										type='text'
										placeholder={search?.label}
										name='s'
										aria-label={search?.label}
									/>

								</form>

								<button
									className={clsx(
										'open-fs-menu',
										isShown && 'active'
									)}
									onClick={openCloseMenu}
									aria-label='Meny'
									aria-controls='fs-menu'
									aria-expanded={isShown}
								>

									<span className='open'>
										<UxMenu />
									</span>

									<span className='close'>
										<UxClose />
									</span>

								</button>

							</div>

						</div>
					</div>

					{/* fs menu */}
					<div id='fs-menu'>
						<div className='container'>
							<div className='row'>
								<div className='col-xl-10 offset-xl-1'>

									<form
										action={search?.href || '/pages/search'}
										className='search'
										method='get'
									>

										<button
											aria-label={search?.label}
											type='submit'
										>
											<UxSearch />
										</button>

										<input
											type='text'
											placeholder={search?.label}
											name='s'
											aria-label={search?.label}
										/>

									</form>

									{isNonWhitespaceString(fsMenu?.button?.label) && (
										<Button
											href={fsMenu?.button?.href}
											target={fsMenu?.button?.target}
											style='solid'
											color='tertiary'
										>
											{fsMenu?.button?.label}
										</Button>
									)}

									<div className='cols'>
										{fsMenu?.cols.map((col, i) => (
											<ul
												className='menu'
												key={i}
											>
												{col.map((item, i) => (
													<li key={i}>
														<a
															href={item.href}
															target={item.target === '_blank' ? '_blank' : undefined}
                                                            rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
															className='hover-underline h3 white'
														>
															{item.label}
														</a>
													</li>
												))}
											</ul>
										))}
									</div>

								</div>
							</div>
						</div>
					</div>

				</div>
			</Section>

		</>
	)
}
